import {
  Component,
  computed,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { $package } from '../../../../../../shared/constants/packaging';
import { IPackage } from '../../../../../../shared/interfaces/packaging.interface';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-package-selector',
  templateUrl: './package-selector.component.html',
  styleUrls: ['./package-selector.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, SharedModule],
})
export class PackageSelectorComponent implements OnInit, OnDestroy {
  public readonly $package: typeof $package = $package;
  public readonly form: InputSignal<UntypedFormGroup> = input.required();
  public readonly fieldName: InputSignal<string> = input.required();
  public readonly packages: InputSignal<IPackage[]> = input.required();
  public readonly isDisabled: InputSignal<boolean> = input(false);
  public readonly formValidationRequest$: InputSignal<Subject<void>> = input.required();
  public readonly packageSelected: OutputEmitterRef<IPackage> = output<IPackage>();
  public readonly packageCleared: OutputEmitterRef<void> = output<void>();

  public showLegacyCustomerPackages: WritableSignal<boolean> = signal<boolean>(false);

  public displayPackages: Signal<IPackage[]> = computed(() => {
    const showLegacy: boolean = this.showLegacyCustomerPackages();
    const packages: IPackage[] = this.packages() ?? [];
    return packages
      .sort((a: IPackage, b: IPackage) => b[$package.roleIds].length - a[$package.roleIds].length)
      .filter((pkg: IPackage) => pkg[$package.legacy] === showLegacy);
  });

  private subscription: Subscription = new Subscription();

  public ngOnInit(): void {
    const form: UntypedFormGroup = this.form();
    const fieldName: string = this.fieldName();

    this.subscription.add(
      form
        .get(fieldName)
        .valueChanges.pipe(startWith(form.get(fieldName).value))
        .subscribe((selectedPackage: IPackage) => {
          if (SharedCommonUtility.notNullish(selectedPackage)) {
            this.showLegacyCustomerPackages.set(selectedPackage[$package.legacy] === true);
          }
        }),
    );

    this.subscription.add(
      this.formValidationRequest$().subscribe(() => {
        form.get(fieldName).markAsDirty();
        form.get(fieldName).markAsTouched();
      }),
    );
  }

  public toggleLegacyCustomerPackages(value: boolean): void {
    this.showLegacyCustomerPackages.set(value);

    const form: UntypedFormGroup = this.form();
    const fieldName: string = this.fieldName();
    form.get(fieldName).setValue(null);
    form.get(fieldName).markAsDirty();
    form.get(fieldName).markAsTouched();
    this.packageCleared.emit();
  }

  public isPackageSelected(pkg: IPackage): boolean {
    return this.form().get(this.fieldName()).value?.[$package._id] === pkg[$package._id];
  }

  public onPackageChange(pkg: IPackage): void {
    this.packageSelected.emit(pkg);
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
